import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function AdminLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const fd = new FormData(e.currentTarget);
    try {
      await login(fd.get("email"), fd.get("password"));
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Login failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/40 px-4 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 bee-grid-bg opacity-50" />
      <div className="absolute -top-40 -right-32 h-[420px] w-[420px] rounded-full bg-bee-honey/20 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 h-[420px] w-[420px] rounded-full bg-bee-teal/15 blur-3xl" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="text-sm text-muted-foreground hover:text-bee-teal inline-flex items-center gap-1.5 mb-6" data-testid="admin-back-home">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
        <div className="rounded-3xl bg-card border border-border shadow-2xl p-8">
          <div className="h-12 w-12 rounded-xl bg-bee-teal text-white flex items-center justify-center">
            <Lock className="h-5 w-5" />
          </div>
          <h1 className="mt-5 font-display text-3xl font-bold tracking-tight">Admin Portal</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in to manage jobs, inquiries and content.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="email" type="email" required data-testid="admin-login-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" name="password" type="password" required data-testid="admin-login-password" />
            </div>
            {error && (
              <div className="text-sm text-destructive font-medium" data-testid="admin-login-error">{error}</div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full h-11 bg-bee-teal text-white hover:bg-[hsl(var(--bee-teal-dark))] rounded-full"
            >
              {submitting ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
